// Client mirror of hvaccollector::compose::score_wizard for the ask-hvac
// repair-or-replace assessment, so the verdict appears instantly.
//
// RUST IS THE AUTHORITY. This exists only for display; the score relayed
// with a lead is always recomputed server-side from the same contract by
// replaying the answers. Weights live in
// shared/contracts/decision-tree.askhvac-repair-replace.v1.json — only the
// two dollar rules below are code, and they are duplicated here and in
// compose.rs. Change one, change the other.
import tree from '@contracts/decision-tree.askhvac-repair-replace.v1.json'

/**
 * The replacement figure the 50% rule measures a repair quote against.
 *
 * This is the TOOL'S WORKING ASSUMPTION, and the factor text now says so.
 * It used to print as "a $9000 typical replacement", which stated a market
 * price as fact — exactly what the site stopped doing in the open-items
 * review (item 1), and inconsistent with the $8,500–$25,000+ planning range
 * now published on /cost-guides.
 *
 * Note for whoever retunes this: a LOW assumption makes the rule fire more
 * often, and the rule pushes toward replace. See the open question raised
 * with the number itself.
 */
export const TYPICAL_REPLACEMENT_USD = 9000

export type Band = 'REPAIR' | 'EITHER' | 'REPLACE'

export interface Verdict {
    /** 0–100, leaning replace as it rises. */
    score: number
    band: Band
    /** Plain-English reasons, in the order they were applied. */
    factors: string[]
    /** Complementary split shown to the visitor. */
    replacePct: number
    repairPct: number
}

interface Opt {
    value: string
    label: string
    points?: number
    years?: number
    quote_mid?: number
}

const nodes = tree.nodes as unknown as Record<string, { options?: Opt[] }>

function option(nodeId: string, value: string | null): Opt | undefined {
    if (!value) return undefined
    return nodes[nodeId]?.options?.find((o) => o.value === value)
}

/** `answers` maps node id → selected option value (or null if unanswered). */
export function scoreAssessment(answers: Record<string, string | null>): Verdict {
    let score = 0
    const factors: string[] = []

    for (const [nodeId, value] of Object.entries(answers)) {
        const opt = option(nodeId, value)
        if (opt?.points) {
            score += opt.points
            factors.push(`${opt.label.toLowerCase()} (${opt.points > 0 ? '+' : ''}${opt.points})`)
        }
    }

    const quote = option('quote_band', answers.quote_band ?? null)?.quote_mid ?? 0
    const years = option('age_band', answers.age_band ?? null)?.years

    // The $5,000 rule: age x repair cost above $5,000 leans replace.
    if (years) {
        const product = years * quote
        if (product > 5000) {
            score += 15
            factors.push(
                `$5,000 rule triggered — ${years} yrs x $${quote} = $${product.toFixed(0)} (+15)`,
            )
        }
    }

    // The 50% rule: a quote above half a typical replacement leans replace.
    if (quote > TYPICAL_REPLACEMENT_USD * 0.5) {
        score += 10
        factors.push(
            `50% rule triggered — $${quote} quote exceeds half the $${TYPICAL_REPLACEMENT_USD} replacement figure this tool assumes (+10)`,
        )
    }

    score = Math.max(0, Math.min(100, score))
    const band: Band = score <= 39 ? 'REPAIR' : score <= 59 ? 'EITHER' : 'REPLACE'

    // One model, one axis: the two percentages are complementary. Showing
    // independent numbers that don't sum (as the mockup sketches) would
    // imply a second model we don't have.
    return { score, band, factors, replacePct: score, repairPct: 100 - score }
}
